import { AdElementWidget, resolveWidgetUrl } from '@adflow/types';
import type { ElementAnimationStyle } from '@adflow/utils';
import { FC, useMemo } from 'react';

type Props = {
  id: string;
  name: string;
  data: AdElementWidget['data'];
  position: AdElementWidget['position'];
  size: AdElementWidget['size'];
  rotation?: AdElementWidget['rotation'];
  isPreview?: boolean;
  animationStyle?: ElementAnimationStyle;
  animationTargetId?: string;
};

/**
 * Draws a widget layer as an iframe. A widget whose url fails `resolveWidgetUrl` draws nothing.
 * In the editor and preview (`isPreview`) the iframe ignores the pointer, so a cross-origin
 * page cannot swallow the clicks that select and move the layer.
 */
const WidgetElement: FC<Props> = ({
  id,
  name,
  data,
  position,
  size,
  rotation,
  isPreview,
  animationStyle,
  animationTargetId,
}) => {
  const resolved = useMemo(() => resolveWidgetUrl(data), [data]);

  if (!resolved.ok) {
    return null;
  }

  const transform =
    [animationStyle?.transform, rotation ? `rotate(${rotation}rad)` : undefined]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <iframe
      id={id}
      data-animation-target-id={animationTargetId}
      src={resolved.url}
      title={name || 'Widget'}
      loading="eager"
      width={size.width}
      height={size.height}
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex: position.z,
        border: 0,
        display: 'block',
        pointerEvents: isPreview ? 'none' : undefined,
        transform,
        opacity: animationStyle?.opacity,
      }}
    />
  );
};

WidgetElement.displayName = 'WidgetElement';

export default WidgetElement;
